import {
  gql,
  MutationFunction,
  MutationResult,
  useMutation
} from '@apollo/client'
import { Basket_Response } from 'shared/presenters/graphqlTypes'
import useBasket from './useBasket'

interface RequestToRedeemCodeProps {
  changeViewOnSuccess: () => void
}

export interface RequestToRedeemCodeResult {
  redeemCode: MutationFunction
  redeemCodeLoading: MutationResult['loading']
  redeemCodeError: MutationResult['error']
}

type RequestToRedeemCode = (
  props: RequestToRedeemCodeProps
) => RequestToRedeemCodeResult

/**
 * It calls a mutation to assign a price modifier.
 *
 * This hook helps to lift the state up to the parent component. This works so that when you call the returned
 * redeemCode method from this hook then the parent component can handle updating the basket via a query refetch.
 *
 * In other words, we need it for pages with different views e.g [widgetSlug] and checkout, this mutation is called from one view but
 * it needs to affect the data for the whole page, when we lift the state up to the parent component we make the data available for the whole page.
 *
 * Use this function in combination with the RedeemVoucherModal component to insert a code and redeem a credit voucher, activity voucher or class pass.
 */
const useAssignPriceModifier: RequestToRedeemCode = ({
  changeViewOnSuccess
}) => {
  const { refetchBasket, basket } = useBasket()

  const [redeemCode, { loading: redeemCodeLoading, error: redeemCodeError }] =
    useMutation<{
      Consumer_Baskets_RedeemCode: Basket_Response
    }>(ASSIGN_PRICE_MODIFIER, {
      onCompleted(response) {
        refetchBasket({
          basketId: response.Consumer_Baskets_RedeemCode.basket_id
        })

        /**
         * TODO: Once the backend is providing the data necessary we will need to add a logic to
         * identify the type of assign_price_modifier. It should redirect to the activity page
         * if the redeemed code is an activity voucher.
         */

        /**
         * Redirect to another view on the same page if the redeem code
         * is a credit voucher or purchased class pass
         */
        changeViewOnSuccess()
      }
    })

  return {
    basket,
    redeemCode,
    redeemCodeLoading,
    redeemCodeError
  }
}

export default useAssignPriceModifier

const ASSIGN_PRICE_MODIFIER = gql`
  mutation WidgetUseAssignPriceModifier(
    $basketId: ID
    $code: String!
    $lineItemId: ID
    $outletId: ID
    $widgetSlug: String
  ) {
    Consumer_Baskets_RedeemCode(
      basket_id: $basketId
      code: $code
      line_item_id: $lineItemId
      outlet_id: $outletId
      widget_slug: $widgetSlug
    ) {
      basket_id
      price_after_adjustments_pence
    }
  }
`
